import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { Alert, Button, Skeleton, Tag, Typography } from 'antd'
import { ArrowLeft } from 'lucide-react'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { ROOM_STATUS_ENUM } from '@/features/master-data/index.ts'
import { rentalListSearchSchema, RentalOrderList } from '@/features/order/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'

// 房间范围只由路径决定，查询参数和历史缓存不能覆盖它。
const historySearchSchema = rentalListSearchSchema.omit({ roomId: true, siteId: true })

export const Route = createFileRoute('/_authenticated/address/rooms_/$roomId/rentals')({
  validateSearch: (raw): Partial<ReturnType<typeof historySearchSchema.parse>> => {
    const parsed = historySearchSchema.parse(raw)
    return Object.fromEntries(Object.entries(parsed).filter(([key]) => Object.hasOwn(raw, key)))
  },
  beforeLoad: (options) => {
    const roomId = Number(options.params.roomId)
    if (!Number.isSafeInteger(roomId) || roomId < 1) throw notFound()
    const listQuery = createListQueryPersistence(`super-mega:room:${roomId}:rentals:list-query`, historySearchSchema)
    listQuery.restore(options)
    return { roomId, listQuery }
  },
  component: RoomRentalsPage,
  head: () => ({ meta: [{ title: '房间租赁记录 · Super Mega' }] })
})

function RoomRentalsPage() {
  const { currentSession, roomId, listQuery } = Route.useRouteContext()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate: Route.useNavigate()
  })
  const trpc = useTRPC()
  const room = useQuery(trpc.room.detail.queryOptions({ id: roomId }))
  const record = room.data

  return (
    <div className="bg-page text-content flex min-h-dvh flex-col">
      <WorkspaceHeader className="sticky top-0 z-10 h-auto min-h-15 flex-wrap overflow-x-visible py-3 [&>div]:shrink">
        <div className="min-w-0 flex-1">
          <h1 className="m-0 truncate text-base font-semibold">
            {record ? `${record.site.name} · ${record.roomNo} · 租赁记录` : '房间租赁记录'}
          </h1>
          <p className="text-content-muted m-0 text-xs">房间 ID：{roomId} · 当前房间的在租及历史订单</p>
        </div>
        <Link
          className="border-border text-content! hover:border-accent hover:text-accent! inline-flex min-h-8 shrink-0 items-center gap-2 rounded-md border px-3 text-sm transition-colors"
          to="/address/rooms"
          search={{ current: 1, pageSize: 20, siteId: record?.siteId }}
        >
          <ArrowLeft aria-hidden size={16} />
          返回房源管理
        </Link>
      </WorkspaceHeader>
      {room.isError ? (
        <Alert
          className="m-4"
          showIcon
          type="error"
          title={room.error.message}
          action={<Button onClick={() => void room.refetch()}>重试</Button>}
        />
      ) : record ? (
        <div className="grid min-w-0 flex-1 grid-cols-1 content-start items-start gap-4 p-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <aside aria-label="房间信息" className="flex min-w-0 flex-col gap-4 lg:sticky">
            <section className="bg-surface rounded-xl p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="m-0 text-sm font-semibold">房间信息</h2>
                <Tag className="me-0!" color={ROOM_STATUS_ENUM[record.status].color}>
                  {ROOM_STATUS_ENUM[record.status].text}
                </Tag>
              </div>
              <dl className="m-0 grid grid-cols-2 gap-4 text-sm lg:grid-cols-1">
                <div className="min-w-0">
                  <dt className="text-content-muted mb-1 text-xs">所属场地</dt>
                  <dd className="m-0 break-words">{record.site.name}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-content-muted mb-1 text-xs">房号</dt>
                  <dd className="m-0">
                    <Typography.Text copyable>{record.roomNo}</Typography.Text>
                  </dd>
                </div>
                <div className="col-span-2 min-w-0 lg:col-span-1">
                  <dt className="text-content-muted mb-1 text-xs">完整地址</dt>
                  <dd className="m-0 leading-relaxed break-words">
                    <Typography.Text copyable>{record.fullAddress}</Typography.Text>
                  </dd>
                </div>
                <div className="col-span-2 min-w-0 lg:col-span-1">
                  <dt className="text-content-muted mb-1 text-xs">归属主体</dt>
                  <dd className="m-0 leading-relaxed break-words">{record.site.organization?.name ?? '—'}</dd>
                </div>
              </dl>
            </section>
          </aside>
          <main id="room-rental-orders" className="bg-surface min-w-0 scroll-mt-24 overflow-hidden rounded-xl">
            <RentalOrderList
              key={roomId}
              compact
              roomScoped
              search={{ ...search, roomId }}
              navigateSearch={navigateSearch}
              currentSession={currentSession}
            />
          </main>
        </div>
      ) : (
        <Skeleton active className="p-6" />
      )}
    </div>
  )
}
